import {
    Address,
    AddressDetailDto,
    EMAIL_IFINANCE,
    PHONE_NUMBER_IFINANCE,
    PHONE_NUMBER_LINK_IFINANCE,
    reportErrorToConsole,
    SelectedAddress,
    useAddressDetail,
} from '@applyonline/shared'
import {
    AddressAutoCompleteInput,
    FORM_ID,
    ScrollableAlert,
    useTranslatedOptionEnum,
} from '@applyonline/shared/components'
import { helpTip, removeSinFormat, sinFormat, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import i18n from '@if/core/i18n'
import {
    EFinancingProgram,
    EGender,
    EHomeStatus,
    EProvince,
    getEHomeStatusList,
    getEProvinceList,
} from '@if/core/types'
import { formatAddress } from '@if/core/utils'
import { range } from 'lodash-es'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import ValidationErrorMessage from '../../../../../packages/shared/validator/ValidationErrorMessage'
import { Constants } from '../../../types'
import { PersonalDetails, PersonalDetailsSchema } from '../../../types/Schemas/PrequalificationSchema'
import { useIsUncoveredProvince } from '../hooks'

interface Props {
  applicantData: PersonalDetails
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  onApplicantChanged: (data: PersonalDetails) => void
  setDisableNext: (disable: boolean) => void
  forceProvince?: EProvince
}

const PersonnalDetailsForm = ({
  applicantData,
  nextStepAfter,
  currentStep,
  onApplicantChanged,
  setDisableNext,
  forceProvince,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage: lang },
  } = useTranslation()
  const [birthDateState, setBirthDateState] = useState<Record<string, number | undefined>>({
    year: undefined,
    month: undefined,
    day: undefined,
  })

  const [showAlert, setShowAlert] = useState(false)
  const [isEqualError, setIsEqualError] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress | null>(null)
  const [showAutoCompleteAddressInput, setShowAutoCompleteAddressInput] = useState(true)
  const [addressDetail] = useAddressDetail(EFinancingProgram.B2c)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [daysInMonth, setDaysInMonth] = useState(31)
  const yearSelectRef = useRef<HTMLSelectElement>(null)

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues,
    control,
    subscribe,
  } = useForm<PersonalDetails>({
    mode: 'onBlur',
    resolver: yupResolver(PersonalDetailsSchema),
    defaultValues: applicantData,
  })

  useFormAutoSave(subscribe, onApplicantChanged)

  const watchBirthDate = watch('birthDate')

  const birthDate = useMemo(() => {
    const d = watchBirthDate?.split('-') ?? ['', '', '']
    if (watchBirthDate) {
      return {
        year: Number(d[0]),
        month: Number(d[1]),
        day: Number(d[2]),
      }
    }
    return { year: undefined, month: undefined, day: undefined }
  }, [watchBirthDate])

  if (birthDate !== birthDateState) {
    setBirthDateState(birthDate)
  }

  const fixNumberInTwoDigits = (num: number) => {
    return (num < 10 ? '0' : '') + num
  }

  const handleDate = (event: ChangeEvent<HTMLSelectElement>) => {
    setBirthDateState((birth) => {
      const { value } = event.target
      switch (event.target.id) {
        case 'month':
          birth.month = +value
          break
        case 'year':
          birth.year = +value
          break
        default:
          birth.day = +value
      }

      if (birth.year !== undefined && birth.month !== undefined) {
        const days = new Date(+birth.year, birth.month, 0).getDate()
        setDaysInMonth(days)
        if (birth.day !== undefined && birth.day > days) {
          birth.day = undefined
        }
      }

      if (!Object.values(birth).includes(undefined)) {
        setValue(
          'birthDate',
          `${birth.year}-${fixNumberInTwoDigits(Number(birth.month))}-${fixNumberInTwoDigits(Number(birth.day))}`,
        )
      } else {
        setValue('birthDate', undefined as unknown as string)
      }

      return birth
    })
  }

  const currentDate = new Date()

  const minBirthDate = {
    year: currentDate.getFullYear() - 18,
    month: currentDate.getMonth() + 1,
    day: currentDate.getDate(),
  }

  const maxBirthDate = {
    year: currentDate.getFullYear() - 100,
    month: 1,
    day: 1,
  }

  const handleSinOnChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const sin = removeSinFormat(value)
    setValue('sin', sin)
    await trigger('sin')
  }

  useEffect(() => {
    helpTip()
  }, [])

  const onSubmit = (data: PersonalDetails) => {
    onApplicantChanged(data)
    nextStepAfter(currentStep)
  }

  const homeFeeTypeId = useWatch({
    control,
    name: 'spending.homeFeeTypeId',
    defaultValue: applicantData.spending.homeFeeTypeId,
  })

  const province = watch('currentAddress.stateIso')
  const totalMonthlyHomePayment = watch('spending.totalMonthlyHomePayment')
  const civicNumber = watch('currentAddress.civicNumber')
  const apartmentNumber = watch('currentAddress.apartment')

  const uncoveredProvince = useIsUncoveredProvince(province)

  useEffect(() => {
    setDisableNext(uncoveredProvince || isEqualError)
  }, [isEqualError, uncoveredProvince, setDisableNext])

  const needTotalMonthlyHomePayment = useCallback(() => {
    return homeFeeTypeId !== EHomeStatus.Owner && homeFeeTypeId !== EHomeStatus.WithRelative
  }, [homeFeeTypeId])

  const needMonthlyHomePayment = useCallback(() => {
    return (
      (totalMonthlyHomePayment !== null && totalMonthlyHomePayment > 0) || homeFeeTypeId === EHomeStatus.WithRelative
    )
  }, [totalMonthlyHomePayment, homeFeeTypeId])

  const [previousHomeFeeTypeId, setPreviousHomeFeeTypeId] = useState<EHomeStatus | undefined>(homeFeeTypeId)
  useEffect(() => {
    if (homeFeeTypeId !== previousHomeFeeTypeId) {
      setValue('spending.totalMonthlyHomePayment', null, {
        shouldTouch: true,
        shouldDirty: true,
      })
      setValue('spending.housingMonthly', null, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [homeFeeTypeId, setValue, previousHomeFeeTypeId, setPreviousHomeFeeTypeId, needMonthlyHomePayment])
  useEffect(() => {
    setPreviousHomeFeeTypeId(homeFeeTypeId)
  }, [setPreviousHomeFeeTypeId, homeFeeTypeId])

  const equalNumbers = useMemo(() => {
    return (
      civicNumber === apartmentNumber &&
      civicNumber !== '' &&
      apartmentNumber !== '' &&
      civicNumber !== undefined &&
      apartmentNumber !== undefined
    )
  }, [civicNumber, apartmentNumber])

  useEffect(() => {
    if (!equalNumbers) {
      setIsEqualError(false)
      setShowAlert(false)
      return
    }

    setShowAlert(true)
    setIsEqualError(true)
  }, [equalNumbers])

  const handleClearFields = () => {
    setShowAlert(false)
  }

  const onChangeAddress = async (address: SelectedAddress) => {
    if (!address) return
    if (!executeRecaptcha) {
      reportErrorToConsole('ReCAPTCHA non disponible.')
      return
    }
    const recaptchaToken = await executeRecaptcha('submit_addressDetail')
    const dto: AddressDetailDto = {
      placeId: address.placeId,
      recaptchaToken,
      userLanguageCode: lang!,
    }

    const ret = await addressDetail(dto)

    const currentAddress = {
      civicNumber: ret.address.civicNumber,
      street: ret.address.street,
      apartment: ret.address.apartment ?? '',
      postalCode: ret.address.postalCode,
      city: ret.address.city,
      stateIso: ret.address.stateIso,
      country: ret.address.country,
    }

    Object.entries(currentAddress).forEach(([key, value]) => {
      setValue(`currentAddress.${key as keyof Address}`, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    })
  }

  const hasAddressAutoCompleteInputError = () => {
    if (!errors.currentAddress) return false

    const fieldsToCheck: (keyof typeof errors.currentAddress)[] = [
      'civicNumber',
      'street',
      'postalCode',
      'city',
      'stateIso',
    ]

    return fieldsToCheck.some((field) => errors.currentAddress?.[field])
  }

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const homeStatusOptions = useTranslatedOptionEnum('EHomeStatus', getEHomeStatusList(), 'enum')
  useEffect(() => {
    if (errors.birthDate && yearSelectRef.current) {
      yearSelectRef.current.focus()
      yearSelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [errors.birthDate])
  return (
    <section className="step-content">
      <h3 className="form-question">{t('contact.provideInformation')}</h3>
      <ScrollableAlert
        type="warn"
        showAlert={equalNumbers && showAlert}
        message={t('common.sameCivicAndAppartmentNumber')}
        buttons={[
          {
            id: 'btn-dismiss',
            label: t('common.yes'),
            onClick: () => {
              setShowAlert(false)
              setIsEqualError(false)
            },
          },
          { id: 'btn-correct', label: t('common.modify'), onClick: () => handleClearFields() },
        ]}
      />
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div className={`control-group ${errors?.genderId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.yourGender')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={0}
                id="Male"
                {...register('genderId')}
                defaultChecked={applicantData.genderId === EGender.Men}
              />
              <label htmlFor="Male">{t('personalInformation.male')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={1}
                id="Female"
                {...register('genderId')}
                defaultChecked={applicantData.genderId === EGender.Woman}
              />
              <label htmlFor="Female">{t('personalInformation.female')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={2}
                id="Other"
                {...register('genderId')}
                defaultChecked={applicantData.genderId === EGender.Other}
              />
              <label htmlFor="Other"> {t('personalInformation.preferNotSay')}</label>
            </div>
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="year">{t('personalInformation.yourBirthDate')}</label>
          <div className="control-group date-wrap">
            <div className={`dropdown-wrap ${errors?.birthDate && 'error'}`}>
              <select
                ref={yearSelectRef}
                id="year"
                name="date"
                onChange={handleDate}
                value={birthDate.year?.toString() ?? ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthYear')}
                </option>
                {range(minBirthDate.year, maxBirthDate.year).map((item: number) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors?.birthDate && 'error'}`}>
              <select
                id="month"
                name="date"
                onChange={handleDate}
                value={birthDate.month ? fixNumberInTwoDigits(birthDate.month) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthMonth')}
                </option>
                {Object.keys(Constants.monthNames).map((month) => (
                  <option value={fixNumberInTwoDigits(+month)} key={Constants.monthNames[Number(month)].nameEn}>
                    {i18n.language === 'en'
                      ? Constants.monthNames[Number(month)].nameEn
                      : Constants.monthNames[Number(month)].nameFr}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors?.birthDate && 'error'}`}>
              <select
                id="day"
                name="date"
                onChange={handleDate}
                value={birthDate.day ? fixNumberInTwoDigits(birthDate.day) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthDay')}
                </option>
                {range(1, daysInMonth + 1).map((item: number) => (
                  <option key={item} value={fixNumberInTwoDigits(item)}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ position: 'relative', top: '-1rem' }}>
            <ValidationErrorMessage message={errors.birthDate?.message} />
          </div>
        </div>

        <div className={`control-group  ${errors?.sin && 'error'}`}>
          <label htmlFor="insurance-number">
            {t('personalInformation.sinOptional')}
            <span className="help-tip">
              <p>{t('personalInformation.optionalSin')}</p>
            </span>
          </label>
          <input
            id="insurance-number"
            placeholder="XXX-XXX-XXX"
            className="sin-input"
            type="text"
            inputMode="numeric"
            defaultValue={sinFormat(getValues('sin'))}
            onBlur={handleSinOnChange}
          />
          <ValidationErrorMessage message={errors.sin?.message} />
        </div>

        <ScrollableAlert
          type="warn"
          showAlert={uncoveredProvince}
          message={
            <>
              <h4>{t('common.uncoveredProvincesTitle')}</h4>
              <Trans
                i18nKey="common.uncoveredProvincesDescription"
                values={{
                  phoneNumber: PHONE_NUMBER_IFINANCE,
                  email: EMAIL_IFINANCE,
                }}
                components={{
                  phone: <Link to={PHONE_NUMBER_LINK_IFINANCE} style={{ textWrap: 'nowrap' }} />,
                  email: <Link to={`mailto:${EMAIL_IFINANCE}`} style={{ textWrap: 'nowrap' }} />,
                }}
              />
              <br />
              <Trans
                i18nKey="common.contactUs"
                values={{
                  phoneNumber: PHONE_NUMBER_IFINANCE,
                  email: EMAIL_IFINANCE,
                }}
                components={{
                  phone: <Link to={PHONE_NUMBER_LINK_IFINANCE} style={{ textWrap: 'nowrap' }} />,
                  email: <Link to={`mailto:${EMAIL_IFINANCE}`} style={{ textWrap: 'nowrap' }} />,
                }}
              />
            </>
          }
        />

        {showAutoCompleteAddressInput ? (
          <>
            <AddressAutoCompleteInput
              setShowAutoCompleteAddressInput={setShowAutoCompleteAddressInput}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              onChangeAddress={onChangeAddress}
              fullAddressDisplay={
                applicantData.currentAddress.civicNumber ? formatAddress(applicantData.currentAddress) : ''
              }
              financingProgramId={EFinancingProgram.B2c}
            />
            <ScrollableAlert
              type="warn"
              showAlert={!!errors.currentAddress?.country || hasAddressAutoCompleteInputError()}
              message={
                errors.currentAddress?.country
                  ? t('addressAutoComplete.canadaAddress')
                  : t('addressAutoComplete.refineSearch')
              }
            />
          </>
        ) : (
          <>
            <div
              style={{ marginTop: '1rem' }}
              className={`control-group mid ${errors.currentAddress?.civicNumber || errors.currentAddress?.apartment || isEqualError ? 'error' : ''}`}
            >
              <label htmlFor="civic_number">{t('address.civicNumber')}</label>
              <input
                type="text"
                inputMode="numeric"
                id="civicNumber"
                autoComplete="address-line1"
                {...register('currentAddress.civicNumber')}
                maxLength={10}
              />
              <ValidationErrorMessage message={errors.currentAddress?.civicNumber?.message} />
            </div>

            <div className={`control-group mid  ${errors.currentAddress?.street && 'error'}`}>
              <label htmlFor="street_name">{t('address.streetName')}</label>
              <input type="text" id="street" {...register('currentAddress.street')} maxLength={100} />
              <ValidationErrorMessage message={errors.currentAddress?.street?.message} />
            </div>

            <div className={`control-group mid  ${errors.currentAddress?.city && 'error'}`}>
              <label htmlFor="city">{t('address.city')}</label>
              <input type="text" id="city" {...register('currentAddress.city')} maxLength={50} />
              <ValidationErrorMessage message={errors.currentAddress?.city?.message} />
            </div>

            <div className={`control-group mid  ${errors.currentAddress?.postalCode && 'error'}`}>
              <label htmlFor="postal_code">{t('address.postalCode')}</label>
              <input type="text" id="postalCode" {...register('currentAddress.postalCode')} maxLength={7} />
              <ValidationErrorMessage message={errors.currentAddress?.postalCode?.message} />
            </div>

            <div className="control-group mid">
              <label htmlFor="select">{t('address.province')}</label>
              <div className={`dropdown-wrap ${errors.currentAddress?.stateIso && 'error'}`}>
                <select id="stateIso" {...register('currentAddress.stateIso')} defaultValue="">
                  {!forceProvince && (
                    <>
                      <option value="" disabled>
                        {' '}
                      </option>
                      {provinceOptions}
                    </>
                  )}
                  {forceProvince && (
                    <option value={forceProvince}>{t(`EProvince.${forceProvince}`, { ns: 'enum' })}</option>
                  )}
                </select>
                <ValidationErrorMessage message={errors.currentAddress?.stateIso?.message} />
              </div>
            </div>
          </>
        )}

        <div
          className={`control-group mid ${errors.currentAddress?.apartment ? 'error' : ''} ${
            isEqualError ? 'error' : ''
          }`}
        >
          <label htmlFor="apartment_number">{t('address.apartmentNumber')}</label>
          <input type="text" id="apartment" {...register('currentAddress.apartment')} maxLength={50} />
          <ValidationErrorMessage message={errors.currentAddress?.apartment?.message} />
        </div>

        <div className="control-group" style={{ paddingBottom: 0 }}>
          <label htmlFor="year" style={{ marginBottom: 0 }}>
            {t('livingAtAddressSince.howLong')}
            <span className="help-tip">
              <p>{t('livingAtAddressSince.details')}</p>
            </span>
          </label>
        </div>

        <div className="control-group">
          <div className={`dropdown-wrap ${errors.currentAddress?.months && 'error'}`}>
            <select id="months" {...register('currentAddress.months')} defaultValue={0}>
              <option value={0}>{t('common.years')}</option>
              <option value={11}>{t('common.lessThan1year')}</option>
              {range(1, 12).map((year) => (
                <option value={year * 12} key={year}>
                  {t('common.yearsLabel', { count: year })}
                </option>
              ))}
              <option value={12}>
                {t('common.moreThan')} {t('common.yearsLabel', { count: 12 })}
              </option>
            </select>
            <ValidationErrorMessage message={errors.currentAddress?.months?.message} />
          </div>
        </div>

        <div className="control-group" style={{ paddingBottom: 0 }}>
          <label htmlFor="housing_status" style={{ marginBottom: 0 }}>
            {t('housing.currentStatus')}
            <span className="help-tip">
              <p>{t('livingAtAddressSince.details')}</p>
            </span>
          </label>
        </div>

        <div className="control-group mid">
          <label htmlFor="housing_status">{t('housing.homeFeeType')}</label>
          <div className={`dropdown-wrap ${errors.spending?.homeFeeTypeId && 'error'}`}>
            <select {...register('spending.homeFeeTypeId')} id="homeFeeTypeId">
              {homeStatusOptions}
            </select>
          </div>
        </div>
        {needTotalMonthlyHomePayment() && (
          <div className={`control-group mid  ${errors.spending?.totalMonthlyHomePayment && 'error'}`}>
            <label htmlFor="mortgage_amount">
              {t('housing.totalMonthlyPayment', { homeFeeType: t(`housing.${homeFeeTypeId}`) })}
            </label>
            <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
              <input
                type="number"
                inputMode="decimal"
                id="totalMonthlyHomePayment"
                {...register('spending.totalMonthlyHomePayment')}
              />
            </div>
            {errors.spending?.totalMonthlyHomePayment && (
              <ValidationErrorMessage message={errors.spending?.totalMonthlyHomePayment?.message} />
            )}
          </div>
        )}
        {needMonthlyHomePayment() && (
          <div className={`control-group  ${errors.spending?.housingMonthly && 'error'}`}>
            <label htmlFor="mortgage_payment">
              {t('housing.contributedMonthlyPayment', {
                homeFeeType: t(`housing.${homeFeeTypeId}`),
              })}
            </label>
            <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
              <input
                type="number"
                inputMode="decimal"
                id="monthlyHomePayment"
                {...register('spending.housingMonthly')}
              />
            </div>
            {errors.spending?.housingMonthly && (
              <ValidationErrorMessage message={errors.spending?.housingMonthly?.message} />
            )}
          </div>
        )}
      </form>
    </section>
  )
}
export default PersonnalDetailsForm
